import { UnapprovedTag } from "../components/quote-builder/UnapprovedSupplierDialog";
import { lineSupplier, lineItem } from "../lib/suppliers";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, gbp, openFile } from "../lib/apiClient";
import { Pencil, Trash2, Printer, ArrowLeft, GitBranch, RefreshCw, Copy, Download, Paperclip, ClipboardList } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { useConfirm } from "../components/ConfirmDialog";
import { AttachmentsBadge } from "../components/AttachmentsBadge";
import { runQuoteMatrixExport } from "../lib/pdfExport";
import { ApprovalSlider } from "../components/quote-builder/ApprovalSlider";

// Compact UK date for markup-override audit stamp: "14 Feb 26 14:32"
function fmtAuditDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${yy} ${hh}:${mm}`;
}

export default function QuoteDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [versions, setVersions] = useState([]);
  const [reviseBusy, setReviseBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [canApprove, setCanApprove] = useState(false);

  const [pdfTemplate, setPdfTemplate] = useState(null);
  const [costSettings, setCostSettings] = useState({ cost_label: "Cost", sell_label: "Sell price to client", labour_cost_rate: 45 });
  const confirm = useConfirm();

  useEffect(() => {
    setQ(null);
    api.get(`/quotes/${id}`).then((r) => setQ(r.data));
    api.get(`/quotes/${id}/versions`).then((r) => setVersions(r.data.versions || []));
    api.get(`/quotes/${id}/attachments`).then((r) => setAttachments(r.data.attachments || [])).catch(() => {});
    api.get(`/pdf-template`).then((r) => setPdfTemplate(r.data)).catch(() => setPdfTemplate(null));
    api.get(`/cost-settings`).then((r) => setCostSettings(r.data || costSettings)).catch(() => {});
    api.get(`/quote-approvals/settings`).then((r) => setCanApprove(!!r.data.can_approve)).catch(() => {});
  }, [id]);

  if (!q) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${q.quote_number}?`,
      description: "This cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/quotes/${id}`);
    nav("/quotes");
  };

  const revise = async () => {
    setReviseBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/revise`);
      nav(`/quotes/${data.id}/edit`);
    } finally {
      setReviseBusy(false);
    }
  };

  const duplicate = async () => {
    setReviseBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      nav(`/quotes/${data.id}/edit`);
    } finally {
      setReviseBusy(false);
    }
  };

  const exportCombinedPdf = () => runQuoteMatrixExport({
    q,
    attachments,
    includeAttachments,
    id,
    api,
    toast,
    setBusy: setExportBusy,
    fmtAuditDate,
  });

  const labourLines = (q.labour_items || []).map((lb) => {
    const mult = (lb.difficulty === "Low" || lb.difficulty === "Medium") ? 1.1 : lb.difficulty === "High" ? 1.2 : 1;
    const lineMult = Number(lb.multiplier ?? 1) || 1;
    const rate = Number(lb.branch_rate || 0);
    const hours = Number(lb.hours || 0);
    const subtotal = Number(lb.line_cost ?? hours * mult * lineMult * rate);
    const productLabel = lb.product_name === "__CUSTOM__"
      ? (lb.custom_product || "Custom")
      : (lb.product_name || "—");
    return { lb, rate, subtotal, productLabel, lineMult };
  });

  const materialLines = (q.line_items || []).map((li) => {
    const qty = Number(li.quantity || 0);
    const cost = Number(li.supplier_cost || 0);
    const mp = Number(li.markup_percent || 0) / 100;
    const lineTotal = Number(li.line_total ?? qty * cost * (1 + mp));
    return { li, lineTotal };
  });

  const breakdown = [
    ["Materials (with markup)", q.materials_total, "blue"],
    ["Labour", q.labour_subtotal, "orange"],
    ["Travel", q.travel_cost, "slate"],
    ["Access Hire", q.access_hire_total, "slate"],
    ["Hotel", q.hotel_total, "purple"],
    ["Sundries", q.sundry_cost, "slate"],
    [`Contingency Sundry (${Number(q.contingency_sundry_percent || 0)}%)`, q.contingency_sundry_amount, "slate"],
    [`Administration Charge (${Number(q.administration_percent || 0)}%)`, q.administration_amount, "slate"],
    ["Callout", q.callout_fee, "slate"],
    ["Hotel", q.hotel_cost, "slate"],
    ["Food", q.food_total, "slate"],
    ["Extras", q.extras_total, "orange"],
    ["Client Surcharge", q.customer_surcharge, "orange"],
  ].filter(([, v]) => Number(v) > 0);

  const locked = !!q.approval_required && !q.approved;
  const lockTitle = locked ? "This quote needs approving before it can be printed or exported" : undefined;

  return (
    <div className={`space-y-6 fade-up print-page ${locked ? "print-locked" : ""}`} data-testid="quote-detail-page">
      {/* Toolbar — hidden in print */}
      <div className="flex items-center justify-between gap-2 no-print flex-wrap">
        <Link to="/quotes" className="text-xs uppercase tracking-wider text-slate-500 hover:text-brand-blue inline-flex items-center gap-1 font-semibold">
          <ArrowLeft size={14} /> Back to quotes
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {q.approval_required && <ApprovalSlider quote={q} canApprove={canApprove} onChange={setQ} />}
          <button onClick={() => window.print()} disabled={locked} title={lockTitle} className="h-9 px-4 rounded-full border border-slate-300 hover:border-brand-blue hover:text-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:text-inherit" data-testid="print-button">
            <Printer size={14} /> Print
          </button>
          <button
            onClick={exportCombinedPdf}
            disabled={exportBusy || locked}
            className="h-9 px-4 rounded-full bg-brand-blue text-white hover:bg-brand-blue-dark text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-60 transition-colors"
            data-testid="export-pdf-button"
            title={lockTitle || (includeAttachments && attachments.length > 0
              ? `Export Quote Matrix + ${attachments.length} attachment${attachments.length === 1 ? "" : "s"} as one PDF`
              : "Export the internal Quote Matrix (planning & purchasing sheet)")}
          >
            <Download size={14} /> {exportBusy ? "Building PDF…" : "Export Quote Matrix"}
          </button>
          {attachments.length > 0 && (
            <label
              className="h-9 px-3 inline-flex items-center gap-2 rounded-full border border-slate-300 text-xs text-slate-700 cursor-pointer hover:bg-slate-50"
              title="Include uploaded supplier documents in the exported PDF"
            >
              <input
                type="checkbox"
                checked={includeAttachments}
                onChange={(e) => setIncludeAttachments(e.target.checked)}
                className="h-3.5 w-3.5"
                data-testid="include-attachments-toggle"
              />
              <Paperclip size={12} />
              <span>Include {attachments.length} attachment{attachments.length === 1 ? "" : "s"}</span>
            </label>
          )}
          <Link to={`/quotes/${id}/edit`} className="h-9 px-4 rounded-full border border-slate-300 hover:border-brand-blue hover:text-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 transition-colors" data-testid="edit-quote-button">
            <Pencil size={14} /> Edit
          </Link>
          {q?.survey_pdf_file_id && (
            <a
              href="#" onClick={(e) => { e.preventDefault(); openFile(q.survey_pdf_file_id, q.id); }}
              data-testid="download-survey-pdf"
              className="h-9 px-4 rounded-full border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 transition-colors"
              title="Download the site survey form so you can share it with a supplier"
            >
              <ClipboardList size={14} /> Survey PDF
            </a>
          )}
          <button onClick={revise} disabled={reviseBusy} className="h-9 px-4 rounded-full border border-slate-300 hover:border-brand-blue hover:text-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-60 transition-colors" data-testid="revise-quote-button">
            <RefreshCw size={14} /> {reviseBusy ? "Working…" : "Re-quote (new version)"}
          </button>
          <button onClick={duplicate} disabled={reviseBusy} className="h-9 px-4 rounded-full bg-brand-blue text-white hover:bg-brand-blue-dark text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-60 transition-colors" data-testid="duplicate-quote-button">
            <Copy size={14} /> Duplicate as new calc
          </button>
          <button onClick={remove} className="h-9 px-4 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 transition-colors">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {versions.length > 1 && (
        <div className="qb-card border-brand-blue/30 p-4 no-print" data-testid="version-history-panel">
          <div className="flex items-center gap-2 mb-3">
            <GitBranch size={14} className="text-brand-blue" />
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-blue">
              Version history · {versions.length} versions
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 ml-auto">
              Viewing <span className="font-mono text-brand-blue font-bold">v{q.version || 1}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {versions.map((v) => (
              <Link
                key={v.id}
                to={`/quotes/${v.id}`}
                data-testid={`version-link-${v.version}`}
                className={`px-3 py-2 rounded-lg text-xs inline-flex items-center gap-2 transition-colors ${
                  v.id === id
                    ? "bg-brand-blue text-white"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}
              >
                <span className="font-mono font-bold">v{v.version}</span>
                <span className="font-mono">{v.quote_number}</span>
                <span className="opacity-70">·</span>
                <span className="font-mono">{gbp(v.grand_total)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Printed quote document. On mobile, wrapped in a horizontal scroller so
          the user can pan across the wide PDF-section grids without breaking
          the print layout (the wrapper has no print effect). */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <article className="qb-card overflow-hidden min-w-[760px] xl:min-w-0" id="print-area">
        {/* Header band */}
        <div className="relative bg-gradient-to-br from-brand-blue to-brand-blue-dark text-white p-8 print-header-band">
          <div className="absolute top-0 left-0 right-0 h-1 bg-brand-orange" />
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="bg-white rounded-xl p-2 shadow-sm">
                <img src="/brand/entry-group-logo.jpeg" alt="Entry Group" className="h-14 w-auto rounded-md" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-bold">Quote Matrix</div>
                <h1 className="qb-section-title text-3xl text-white mt-1">{q.quote_number}</h1>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="qb-pill bg-white/15 text-white">
                    {q.status}
                  </span>
                  {q.version > 1 && (
                    <span className="qb-pill bg-brand-orange text-white" data-testid="quote-version-badge">
                      Version {q.version}
                    </span>
                  )}
                  <AttachmentsBadge attachments={attachments} />
                  {q.job_days > 1 && (
                    <span className="qb-pill bg-white/15 text-white inline-flex items-center gap-1.5" data-testid="quote-multi-day-badge" title={`Travel labour was multiplied by ${q.job_days} for one round-trip per day`}>
                      📅 {q.job_days} days
                    </span>
                  )}
                  {q.revised_from_quote_number && (
                    <span className="text-[10px] uppercase tracking-wider text-white/70">
                      Revised from {q.revised_from_quote_number}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right text-white/90">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Issued</div>
              <div className="font-mono text-sm">{q.created_at?.split("T")[0]}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mt-2">Prepared by</div>
              <div className="text-sm font-medium">{q.estimator || q.created_by_name}</div>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-8">
          {/* Customer & Job grid */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <KV k="Customer" v={q.customer_name} accent />
            <KV k="Site" v={q.site_name || "—"} />
            <KV k="Site Address" v={q.site_address || "—"} />
            <KV k="Postcode" v={q.postcode || "—"} />
            <KV k="Job Type" v={q.job_type} />
            <KV k="Labour Type" v={q.branch || "—"} />
            <KV k="Mark-up" v={`${q.markup_percent ?? 35}%`} />
            {q.labour_only && (
              <div className="col-span-2 md:col-span-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Quote Type</div>
                <div className="mt-1 inline-flex items-center px-2.5 py-1 rounded-full bg-brand-orange text-white text-xs font-semibold" data-testid="labour-only-badge">
                  Labour Only
                </div>
              </div>
            )}
          </section>

          {q.description && (
            <section>
              <SectionHeading>Scope of works</SectionHeading>
              <div className="bg-slate-50 rounded-xl p-5 text-sm leading-relaxed whitespace-pre-wrap text-slate-700">
                {q.description}
              </div>
            </section>
          )}

          {q.health_safety_notes && (
            <section data-testid="quote-hs-section">
              <SectionHeading>Health &amp; Safety Considerations</SectionHeading>
              <div className="bg-orange-50 border-l-4 border-brand-orange rounded-r-xl p-5 text-sm leading-relaxed whitespace-pre-wrap text-slate-800">
                {q.health_safety_notes}
              </div>
            </section>
          )}

          {materialLines.length > 0 && (
            <section>
              <SectionHeading>Parts & Materials</SectionHeading>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-brand-blue text-white">
                    <tr>
                      {["Supplier", "Item", "Description", "Qty", "Cost ex VAT", "Mark-up %", "Mark-up £", "Line Total"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {materialLines.map(({ li, lineTotal }, i) => (
                      <tr key={li.id || i} className={`align-top ${i % 2 === 1 ? "bg-slate-50/50" : ""} border-b border-slate-100 last:border-b-0`}>
                        <td className="px-3 py-3 font-medium text-slate-900 w-32" data-testid={`detail-line-supplier-${i}`}>{lineSupplier(li) || "—"}<div><UnapprovedTag rec={li.unapproved_supplier} testid={`detail-line-unapproved-${li.id}`} /></div></td>
                        <td className="px-3 py-3 text-slate-800 w-32" data-testid={`detail-line-item-${i}`}>{lineItem(li) || "—"}</td>
                        <td className="px-3 py-3 text-slate-700">
                          {li.pdf_line_items && li.pdf_line_items.length > 0 ? (
                            <div className="space-y-1">
                              {li.supplier_quote_ref && (
                                <div className="text-sm font-semibold text-brand-blue">Ref: {li.supplier_quote_ref}</div>
                              )}
                              <table className="w-full text-[12px] border border-slate-200 rounded-md overflow-hidden">
                                <thead className="bg-slate-100/70">
                                  <tr>
                                    <th className="text-left px-2 py-1 text-[9px] uppercase tracking-wider text-slate-600 font-semibold">Item</th>
                                    <th className="text-right px-2 py-1 text-[9px] uppercase tracking-wider text-slate-600 font-semibold">Qty</th>
                                    <th className="text-right px-2 py-1 text-[9px] uppercase tracking-wider text-slate-600 font-semibold">Unit</th>
                                    <th className="text-right px-2 py-1 text-[9px] uppercase tracking-wider text-slate-600 font-semibold">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {li.pdf_line_items.map((pi, j) => (
                                    <tr key={j} className="border-t border-slate-100">
                                      <td className="px-2 py-1">{pi.description}</td>
                                      <td className="px-2 py-1 text-right font-mono">{pi.quantity ?? "—"}</td>
                                      <td className="px-2 py-1 text-right font-mono">{pi.unit_cost != null ? gbp(pi.unit_cost) : "—"}</td>
                                      <td className="px-2 py-1 text-right font-mono">{pi.line_total != null ? gbp(pi.line_total) : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <span className="whitespace-pre-wrap leading-snug">{li.supplier_description || "—"}</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700 align-top">{li.quantity}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700 align-top">{gbp(li.supplier_cost)}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-500 align-top">
                          {li.markup_percent}%
                          {li.markup_override_by && (
                            <div className="text-[9px] uppercase tracking-wider text-brand-orange font-semibold mt-0.5 whitespace-nowrap" data-testid={`materials-markup-audit-${li.id}`}>
                              {li.markup_override_by} · {fmtAuditDate(li.markup_override_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-slate-500 align-top">{gbp(Number(li.quantity || 0) * Number(li.supplier_cost || 0) * Number(li.markup_percent || 0) / 100)}</td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900 align-top">{gbp(lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {labourLines.length > 0 && (
            <section>
              <SectionHeading>Labour</SectionHeading>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-brand-orange text-white">
                    <tr>
                      {(q.job_type === "Repair"
                          ? ["Labour Type", "Work", "Hours", "Mult.", "Difficulty", "Rate £/hr", "Subtotal"]
                          : ["Labour Type", "Product", "Work", "Hours", "Mult.", "Difficulty", "Rate £/hr", "Subtotal"]
                       ).map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold ${i >= (q.job_type === "Repair" ? 2 : 3) ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {labourLines.map(({ lb, rate, subtotal, productLabel, lineMult }, i) => (
                      <tr key={lb.id || i} className={`${i % 2 === 1 ? "bg-slate-50/50" : ""} border-b border-slate-100 last:border-b-0`}>
                        <td className="px-3 py-3 font-medium text-slate-900">{lb.branch || q.branch || "—"}</td>
                        {q.job_type !== "Repair" && <td className="px-3 py-3 text-slate-700">{productLabel}</td>}
                        <td className="px-3 py-3 text-slate-700">{lb.work_type}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700">
                          {lb.hours}
                          {lb.hours_override_by && (
                            <div className="text-[9px] text-slate-500 mt-0.5 font-mono">
                              {lb.hours_override_by} · {fmtAuditDate(lb.hours_override_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700">{lineMult}x</td>
                        <td className="px-3 py-3 text-right text-slate-700">{lb.difficulty}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-700">{rate ? gbp(rate * (lineMult || 1)) : "—"}</td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900">{gbp(subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {(q.access_hire || []).length > 0 && (
            <section data-testid="detail-access-hire-section">
              <SectionHeading>Access Hire</SectionHeading>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-brand-orange text-white">
                    <tr>
                      {["Product", "Type", "Actual product", "Days", "Qty", "Cost ex VAT", "Markup %", "Markup £", "Line Total"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(q.access_hire || []).map((ah, i) => {
                      const days = Number(ah.days || 0);
                      const qty = Number(ah.quantity || 0);
                      const cost = Number(ah.supplier_cost || 0);
                      const mp = Number(ah.markup_percent || 0);
                      const sub = days * qty * cost;
                      const markupVal = sub * mp / 100;
                      const lineTotal = ah.line_total != null ? Number(ah.line_total) : sub + markupVal;
                      return (
                        <tr key={ah.id || i} className={`${i % 2 === 1 ? "bg-slate-50/50" : ""} border-b border-slate-100 last:border-b-0`} data-testid={`detail-access-hire-row-${i}`}>
                          <td className="px-3 py-3 font-medium text-slate-900">
                            {ah.product || "—"}
                            {ah.supplier_quote_ref && (
                              <div className="text-sm text-brand-blue font-semibold mt-0.5">Ref: {ah.supplier_quote_ref}</div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-slate-700">{ah.type || "—"}</td>
                          <td className="px-3 py-3 text-slate-700">{ah.actual_product || ah.supplier_name || "—"}{ah.supplier_name && ah.actual_product ? <div className="text-xs text-slate-500">{ah.supplier_name}</div> : null}<div><UnapprovedTag rec={ah.unapproved_supplier} testid={`detail-ah-unapproved-${ah.id || ""}`} /></div></td>
                          <td className="px-3 py-3 text-right font-mono text-slate-700">{days}</td>
                          <td className="px-3 py-3 text-right font-mono text-slate-700">{qty}</td>
                          <td className="px-3 py-3 text-right font-mono text-slate-700">{gbp(cost)}</td>
                          <td className="px-3 py-3 text-right font-mono text-slate-500">
                            {mp}%
                            {ah.markup_override_by && (
                              <div className="text-[9px] uppercase tracking-wider text-brand-orange font-semibold mt-0.5 whitespace-nowrap">
                                {ah.markup_override_by} · {fmtAuditDate(ah.markup_override_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-slate-500">{gbp(markupVal)}</td>
                          <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900">{gbp(lineTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {(q.sundries || []).length > 0 && (
            <section data-testid="detail-sundries-section">
              <SectionHeading>Sundries</SectionHeading>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700 text-white">
                    <tr>
                      {["Name", "Description", "Line Total"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold ${i < 2 ? "text-left" : "text-right"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(q.sundries || []).map((s, i) => (
                      <tr key={s.id || i} className={`${i % 2 === 1 ? "bg-slate-50/50" : ""} border-b border-slate-100 last:border-b-0`} data-testid={`detail-sundry-row-${i}`}>
                        <td className="px-3 py-3 text-slate-900 font-medium">{s.name || "—"}</td>
                        <td className="px-3 py-3 text-slate-800">{s.description || "—"}</td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900" data-testid={`detail-sundry-total-${i}`}>{gbp(Number(s.line_total ?? s.cost ?? 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {(q.callouts || []).length > 0 && (
            <section data-testid="detail-callouts-section">
              <SectionHeading>Callouts</SectionHeading>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700 text-white">
                    <tr>
                      {["Description", "Cost"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(q.callouts || []).map((c, i) => (
                      <tr key={c.id || i} className={`${i % 2 === 1 ? "bg-slate-50/50" : ""} border-b border-slate-100 last:border-b-0`} data-testid={`detail-callout-row-${i}`}>
                        <td className="px-3 py-3 text-slate-800">{c.description || "—"}</td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900">{gbp(Number(c.cost || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          <section className="grid md:grid-cols-5 gap-6">
            <div className="md:col-span-3">
              <SectionHeading>Cost breakdown</SectionHeading>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                {breakdown.map(([label, val, accent]) => (
                  <div key={label} className="flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-b-0">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <span className={`w-2 h-2 rounded-full ${accent === "blue" ? "bg-brand-blue" : accent === "orange" ? "bg-brand-orange" : "bg-slate-300"}`} />
                      {label}
                    </div>
                    <span className="font-mono text-sm font-semibold text-slate-900">{gbp(val)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col justify-end gap-3">
              <div className="rounded-2xl p-5 bg-slate-100 border-2 border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-600 font-bold" data-testid="detail-cost-label">{costSettings.cost_label || "Cost"}</div>
                <div className="font-mono text-3xl font-bold tracking-tight mt-1.5 text-slate-800" data-testid="detail-total-cost">
                  {gbp(q.total_cost || 0)}
                </div>
              </div>
              <div className="rounded-2xl p-6 bg-gradient-to-br from-brand-blue to-brand-blue-dark text-white shadow-lg print-grand-total">
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-bold" data-testid="detail-sell-label">{costSettings.sell_label || "Grand Total"}</div>
                <div className="font-mono text-4xl font-bold tracking-tight mt-2" data-testid="detail-grand-total">
                  {gbp(q.grand_total)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/70 mt-3">Excludes VAT</div>
              </div>
            </div>
          </section>

          <footer className="pt-6 border-t border-slate-200 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-slate-400">
            <span>Entry Group · Quote Matrix</span>
            <span>{q.quote_number} · v{q.version || 1}</span>
          </footer>
        </div>
      </article>
      </div>

      <PlannerSheet q={q} labourLines={labourLines} materialLines={materialLines} pdfTemplate={pdfTemplate} costSettings={costSettings} />
    </div>
  );
}

function KV({ k, v, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">{k}</div>
      <div className={`text-sm font-medium ${accent ? "text-brand-blue" : "text-slate-900"}`}>{v ?? "—"}</div>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="qb-section-title text-base mb-3 flex items-center gap-2">
      <span className="w-1 h-5 rounded-full bg-brand-orange" />
      {children}
    </div>
  );
}


const HIRE_CATEGORIES = new Set(["Plant Hire", "Access Hire", "Powder Coating", "Transport"]);

function PlannerSheet({ q, labourLines, materialLines, pdfTemplate, costSettings }) {
  // Substitute {keyword} placeholders against quote data
  const subst = (s) => (s || "").replace(/\{(\w+)\}/g, (m, k) => {
    const map = {
      quote_number: q.quote_number || "",
      customer_name: q.customer_name || "",
      job_type: q.job_type || "",
      category: q.category || "",
      branch: q.branch || "",
      postcode: q.postcode || "",
      estimator: q.estimator || q.created_by_name || "",
      version: String(q.version || 1),
      date: new Date().toLocaleDateString("en-GB"),
      status: q.status || "",
    };
    return map[k] != null ? String(map[k]) : m;
  });
  const headerTitle = pdfTemplate?.title || "Planner Sheet · For internal scheduling";
  const headerExtra = pdfTemplate?.header_text ? subst(pdfTemplate.header_text) : "";
  const footerText = pdfTemplate?.footer_text ? subst(pdfTemplate.footer_text) : "";

  // --- Split labour into Travel vs Site hours ----------------------------------
  const travelLabour = labourLines.filter(({ lb }) => (lb.work_type || "") === "Travel");
  const siteLabour = labourLines.filter(({ lb }) => (lb.work_type || "") !== "Travel");
  const travelHours = travelLabour.reduce((s, { lb }) => s + Number(lb.hours || 0), 0)
    + Number(q.travel_hours || 0); // legacy travel-section hours
  const siteHours = siteLabour.reduce((s, { lb }) => s + Number(lb.hours || 0), 0);
  const totalHours = travelHours + siteHours;
  const labourTotal = labourLines.reduce((s, { subtotal }) => s + subtotal, 0);

  // --- Split materials into Materials vs Hire ----------------------------------
  const hireLines = materialLines.filter(({ li }) => HIRE_CATEGORIES.has(li.category));
  const partsLines = materialLines.filter(({ li }) => !HIRE_CATEGORIES.has(li.category));
  const hireTotal = hireLines.reduce((s, { lineTotal }) => s + lineTotal, 0);
  const partsTotal = partsLines.reduce((s, { lineTotal }) => s + lineTotal, 0);

  return (
    <article
      id="planner-sheet"
      data-testid="planner-sheet"
      style={{ display: "none" }}
      className="bg-white text-slate-900"
    >
      {/* HEADER BAND ------------------------------------------------------- */}
      <div
        className="print-header-band text-white px-6 py-5 flex items-start justify-between gap-6"
        style={{ background: "#0E1A33" }}
        data-pdf-section="true"
      >
        <div className="flex items-start gap-4">
          <div className="bg-white rounded-lg p-1.5">
            <img src="/brand/entry-group-logo.jpeg" alt="Entry Group" className="h-10 w-auto rounded" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em]" style={{ color: "#E66A2C" }}>
              {headerTitle}
            </div>
            <div className="font-mono text-2xl font-black tracking-tight mt-1">{q.quote_number}</div>
            <div className="text-[11px] mt-1 opacity-90">
              {q.customer_name} · {q.category || q.job_type}
            </div>
            {headerExtra && (
              <div className="text-[10px] mt-1.5 opacity-80 whitespace-pre-wrap max-w-[420px]">{headerExtra}</div>
            )}
          </div>
        </div>
        <div className="text-right text-[11px] leading-relaxed">
          <div><span className="opacity-70 uppercase tracking-wider text-[9px]">Branch</span><br/>{q.depot || "Head Office"}</div>
          <div className="mt-2"><span className="opacity-70 uppercase tracking-wider text-[9px]">Site postcode</span><br/>{q.postcode || "—"}</div>
          <div className="mt-2"><span className="opacity-70 uppercase tracking-wider text-[9px]">Issued</span><br/>{q.created_at?.split("T")[0]}</div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* BOOKING SCHEDULE FOR BIG CHANGE PLANNERS ---------------------- */}
        {(() => {
          const days = Math.max(1, Number(q.job_days || 1));
          const engineers = Number(q.engineers || 1);
          // On-site minutes = the billed labour + travel hours (matches the labour table).
          const totalMinutes = (q.labour_items || []).reduce((acc, lb) => acc + Number(lb.hours || 0) * 60, 0);
          const minsPerDay = Math.round(totalMinutes / days);
          const fmt = (mins) => {
            const h = Math.floor(mins / 60);
            const m = Math.round(mins - h * 60);
            if (h === 0) return `${m} min`;
            if (m === 0) return `${h} hr`;
            return `${h} hr ${m} min`;
          };
          return (
            <div
              className="flex items-stretch rounded-lg overflow-hidden border-l-4 border border-slate-200"
              style={{ borderLeftColor: "#E66A2C", background: "#FFF4E8" }}
              data-pdf-section="true"
              data-testid="planner-booking-schedule"
            >
              <div className="px-4 py-3 flex-1 grid grid-cols-3 gap-4">
                <div className="col-span-3 text-[9px] uppercase tracking-[0.25em] font-bold" style={{ color: "#B34A11" }}>
                  Booking Schedule  ·  Big Change
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: "#B34A11" }}>Days on site</div>
                  <div className="text-xl font-black text-slate-900 mt-0.5">{days}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: "#B34A11" }}>Engineers</div>
                  <div className="text-xl font-black text-slate-900 mt-0.5">{engineers}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: "#B34A11" }}>Time per day</div>
                  <div className="text-xl font-black text-slate-900 mt-0.5">
                    {totalMinutes > 0 ? (
                      <>{fmt(minsPerDay)} <span className="text-[10px] font-medium text-slate-500">({minsPerDay} min)</span></>
                    ) : "—"}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* AT-A-GLANCE TILES -------------------------------------------- */}
        <div className="grid grid-cols-5 gap-3" data-pdf-section="true">
          <Tile label="Site Hours" value={`${siteHours}h`} accent="#2756C4" />
          <Tile label="Travel Hours" value={`${travelHours}h`} accent="#E66A2C" />
          <Tile label="Total Hours" value={`${totalHours}h`} accent="#0E1A33" big />
          <Tile label="Engineers" value={String(q.engineers || 1)} accent="#6B7280" />
          <Tile label="Job Type" value={q.job_type || "—"} accent="#6B7280" />
        </div>

        {/* SCOPE OF WORKS ----------------------------------------------- */}
        {q.description && (
          <PlannerSection title="What we're proposing to do" color="#2756C4">
            <div className="text-[12px] leading-relaxed text-slate-800 whitespace-pre-wrap p-3 rounded border border-slate-200 bg-slate-50">
              {q.description}
            </div>
          </PlannerSection>
        )}

        {/* LABOUR -------------------------------------------------------- */}
        {labourLines.length > 0 && (
          <PlannerSection title={`Labour · ${labourLines.length} line${labourLines.length === 1 ? "" : "s"}`} color="#E66A2C">
            <PlannerTable
              accent="#E66A2C"
              headers={q.job_type === "Repair"
                ? ["Work", "Labour Type", "Difficulty", "Hours", "Mult.", "Rate £/hr", "Subtotal"]
                : ["Work", "Labour Type", "Product", "Difficulty", "Hours", "Mult.", "Rate £/hr", "Subtotal"]}
              rightCols={q.job_type === "Repair" ? [3, 4, 5, 6] : [4, 5, 6, 7]}
            >
              {/* Travel rows first, visually distinct */}
              {travelLabour.map(({ lb, rate, subtotal, productLabel, lineMult }, i) => (
                <tr key={`tr-${i}`} style={{ background: "#FFF3E8" }}>
                  <td className="px-2 py-1.5 font-semibold" style={{ color: "#C2410C" }}>Travel</td>
                  <td className="px-2 py-1.5">{lb.branch || q.branch || "—"}</td>
                  {q.job_type !== "Repair" && <td className="px-2 py-1.5">{productLabel}</td>}
                  <td className="px-2 py-1.5">{lb.difficulty || "Standard"}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{lb.hours}h</td>
                  <td className="px-2 py-1.5 text-right font-mono">{lineMult}x</td>
                  <td className="px-2 py-1.5 text-right font-mono">{rate ? gbp(rate * (lineMult || 1)) : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{gbp(subtotal)}</td>
                </tr>
              ))}
              {siteLabour.map(({ lb, rate, subtotal, productLabel, lineMult }, i) => (
                <tr key={`sl-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                  <td className="px-2 py-1.5 font-semibold text-slate-900">{lb.work_type}</td>
                  <td className="px-2 py-1.5">{lb.branch || q.branch || "—"}</td>
                  {q.job_type !== "Repair" && <td className="px-2 py-1.5">{productLabel}</td>}
                  <td className="px-2 py-1.5">{lb.difficulty || "Standard"}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">
                    {lb.hours}h
                    {lb.hours_override_by && (
                      <div className="text-[8px] text-slate-500 font-mono leading-none mt-0.5">{lb.hours_override_by} · {fmtAuditDate(lb.hours_override_at)}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{lineMult}x</td>
                  <td className="px-2 py-1.5 text-right font-mono">{rate ? gbp(rate * (lineMult || 1)) : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{gbp(subtotal)}</td>
                </tr>
              ))}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold" colSpan={q.job_type === "Repair" ? 5 : 6}>Totals</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{totalHours}h</td>
                <td className="px-2 py-2 text-right font-mono font-bold" colSpan={2}>{gbp(labourTotal)}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* MATERIALS ----------------------------------------------------- */}
        {partsLines.length > 0 && (
          <PlannerSection title={`Materials · ${partsLines.length} line${partsLines.length === 1 ? "" : "s"}`} color="#2756C4">
            <PlannerTable
              accent="#2756C4"
              headers={["Supplier", "Item", "Description", "Qty", "Cost ex VAT", "Mark-up %", "Mark-up £", "Line Total"]}
              rightCols={[3, 4, 5, 6, 7]}
            >
              {partsLines.flatMap(({ li, lineTotal }, i) => {
                const qty = Number(li.quantity || 0);
                const cost = Number(li.supplier_cost || 0);
                const mp = Number(li.markup_percent || 0);
                const markupVal = qty * cost * (mp / 100);
                const subItems = li.pdf_line_items || [];
                const rows = [
                  <tr key={`m-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                    <td className="px-2 py-1.5 font-medium" rowSpan={subItems.length > 0 ? subItems.length + 1 : 1}>{lineSupplier(li) || "—"}</td>
                    <td className="px-2 py-1.5" rowSpan={subItems.length > 0 ? subItems.length + 1 : 1}>{lineItem(li) || "—"}</td>
                    <td className="px-2 py-1.5 text-[11px] text-slate-700">
                      {li.supplier_quote_ref && (
                        <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded font-mono text-[9px]" style={{ background: "#DBE7FF", color: "#2756C4" }}>
                          Ref · {li.supplier_quote_ref}
                        </span>
                      )}
                      {li.supplier_description || (subItems.length > 0 ? "" : "—")}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{qty}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{gbp(cost)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">
                      {mp}%
                      {li.markup_override_by && (
                        <div style={{ fontSize: "8px", color: "#E66A2C", fontWeight: 700, letterSpacing: "0.05em", marginTop: 2, whiteSpace: "nowrap" }}>
                          {li.markup_override_by} · {fmtAuditDate(li.markup_override_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{gbp(markupVal)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(lineTotal)}</td>
                  </tr>,
                ];
                subItems.forEach((pi, j) => {
                  rows.push(
                    <tr key={`m-${i}-sub-${j}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                      <td className="px-2 py-1 text-[11px] text-slate-600 border-t border-slate-100 pl-4">• {pi.description || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-500 border-t border-slate-100">{pi.quantity ?? ""}</td>
                      <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-500 border-t border-slate-100" colSpan={3}></td>
                      <td className="px-2 py-1 text-right font-mono text-[11px] text-slate-500 border-t border-slate-100">{pi.line_total != null ? gbp(pi.line_total) : ""}</td>
                    </tr>
                  );
                });
                return rows;
              })}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold" colSpan={7}>Materials total</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{gbp(partsTotal)}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* HIRE EQUIPMENT ----------------------------------------------- */}
        {hireLines.length > 0 && (
          <PlannerSection title={`Hire & Equipment · ${hireLines.length} line${hireLines.length === 1 ? "" : "s"}`} color="#16A34A">
            <PlannerTable
              accent="#16A34A"
              headers={["Category", "Supplier", "Description", "Qty", "Line Total"]}
              rightCols={[3, 4]}
            >
              {hireLines.map(({ li, lineTotal }, i) => (
                <tr key={`h-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                  <td className="px-2 py-1.5 font-semibold" style={{ color: "#15803D" }}>{li.category}</td>
                  <td className="px-2 py-1.5">{li.product_name || li.supplier_name || "—"}</td>
                  <td className="px-2 py-1.5 text-[11px] text-slate-700">{li.supplier_description || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{li.quantity}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(lineTotal)}</td>
                </tr>
              ))}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold" colSpan={4}>Hire total</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{gbp(hireTotal)}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* ACCESS HIRE -------------------------------------------------- */}
        {(q.access_hire || []).length > 0 && (
          <PlannerSection title={`Access Hire · ${q.access_hire.length} line${q.access_hire.length === 1 ? "" : "s"}`} color="#E66A2C">
            <PlannerTable
              accent="#E66A2C"
              headers={["Product", "Type", "Actual product", "Days", "Qty", "Cost ex VAT", "Markup %", "Markup £", "Line Total"]}
              rightCols={[3, 4, 5, 6, 7, 8]}
            >
              {(q.access_hire || []).map((ah, i) => {
                const days = Number(ah.days || 0);
                const qty = Number(ah.quantity || 0);
                const cost = Number(ah.supplier_cost || 0);
                const mp = Number(ah.markup_percent || 0);
                const sub = days * qty * cost;
                const markupVal = sub * mp / 100;
                const lineTotal = sub + markupVal;
                return (
                  <tr key={`ah-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                    <td className="px-2 py-1.5 font-semibold" style={{ color: "#C2410C" }}>{ah.product || "—"}</td>
                    <td className="px-2 py-1.5">{ah.type || "—"}</td>
                    <td className="px-2 py-1.5 text-[11px] text-slate-700">{ah.actual_product || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{days}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{qty}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{gbp(cost)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">
                      {mp}%
                      {ah.markup_override_by && (
                        <div style={{ fontSize: "8px", color: "#E66A2C", fontWeight: 700, letterSpacing: "0.05em", marginTop: 2, whiteSpace: "nowrap" }}>
                          {ah.markup_override_by} · {fmtAuditDate(ah.markup_override_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{gbp(markupVal)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(lineTotal)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold" colSpan={8}>Access hire total</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{gbp(q.access_hire_total || 0)}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* SUNDRIES ------------------------------------------------------ */}
        {(q.sundries || []).length > 0 && (
          <PlannerSection title={`Sundries · ${q.sundries.length} line${q.sundries.length === 1 ? "" : "s"}`} color="#0891B2">
            <PlannerTable
              accent="#0891B2"
              headers={["Description", "Qty", "Cost to client", "Markup %", "Line Total"]}
              rightCols={[1, 2, 3, 4]}
            >
              {(q.sundries || []).map((s, i) => {
                const qty = Number(s.quantity || 0);
                const cost = Number(s.supplier_cost || 0);
                const mp = Number(s.markup_percent ?? q.markup_percent ?? 0);
                const lineTotal = Number(
                  s.line_total ?? (qty && cost ? qty * cost * (1 + mp / 100) : (s.cost || 0))
                );
                return (
                  <tr key={s.id || `sund-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""} data-testid={`planner-sundry-row-${i}`}>
                    <td className="px-2 py-1.5 font-medium">{s.description || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{qty || (s.cost != null ? "—" : 0)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{cost ? gbp(cost) : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{qty && cost ? `${mp}%` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(lineTotal)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold" colSpan={4}>Sundries total</td>
                <td className="px-2 py-2 text-right font-mono font-bold" data-testid="planner-sundry-total">{gbp(q.sundry_cost || 0)}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* CALLOUTS section is rendered further down (existing teal block) */}

        {/* HOTEL ------------------------------------------------------- */}
        {q.hotel?.required && ((q.hotel.items || []).length > 0 || (q.hotel.added_costs || 0) > 0) && (          <PlannerSection title={`Hotel · ${(q.hotel.items || []).length} booking${(q.hotel.items || []).length === 1 ? "" : "s"}`} color="#7C3AED">
            <PlannerTable
              accent="#7C3AED"
              headers={["Postcode", "Booking Information", "Rooms", "Nights", "Cost / night", "Markup %", "Markup £", "Line Total"]}
              rightCols={[2, 3, 4, 5, 6, 7]}
            >
              {(q.hotel.items || []).map((h, i) => {
                const rooms = Number(h.rooms ?? (q.hotel.engineers || 1));
                const nights = Number(h.nights || 0);
                const cost = Number(h.cost_per_night || 0);
                const mp = Number(h.markup_percent || 0);
                const sub = rooms * nights * cost;
                const markupVal = sub * mp / 100;
                const lineTotal = sub + markupVal;
                return (
                  <tr key={`h-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                    <td className="px-2 py-1.5 font-mono font-semibold" style={{ color: "#6D28D9" }}>{h.postcode || "—"}</td>
                    <td className="px-2 py-1.5 text-[10px] font-mono break-all">
                      {h.booking_url ? (
                        <a href={h.booking_url} target="_blank" rel="noreferrer" style={{ color: "#6D28D9", textDecoration: "underline" }}>
                          {h.booking_url}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{rooms}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{nights}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{gbp(cost)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{mp}%</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{gbp(markupVal)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(lineTotal)}</td>
                  </tr>
                );
              })}
              {(q.hotel.added_costs || 0) > 0 && (
                <tr style={{ background: "#F3E8FF" }}>
                  <td className="px-2 py-1.5 font-semibold" colSpan={4} style={{ color: "#6D28D9" }}>
                    Added costs (auto)
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold" style={{ color: "#6D28D9" }}>{gbp(q.hotel.added_costs || 0)}</td>
                  <td className="px-2 py-1.5" colSpan={2} />
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(q.hotel.added_costs || 0)}</td>
                </tr>
              )}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold" colSpan={7}>Hotel total</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{gbp(q.hotel.section_total || q.hotel_total || 0)}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}


        {/* CALLOUTS ---------------------------------------------------- */}
        {(q.callouts || []).length > 0 && (
          <PlannerSection title={`Callouts · ${q.callouts.length} line${q.callouts.length === 1 ? "" : "s"}`} color="#0F766E">
            <PlannerTable
              accent="#0F766E"
              headers={["Description", "Cost"]}
              rightCols={[1]}
            >
              {(q.callouts || []).map((c, i) => (
                <tr key={`co-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                  <td className="px-2 py-1.5">{c.description || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(Number(c.cost || 0))}</td>
                </tr>
              ))}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold">Callouts total</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{gbp((q.callouts || []).reduce((s, c) => s + Number(c.cost || 0), 0))}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* EXTRAS ------------------------------------------------------ */}
        {(q.extras || []).length > 0 && (
          <PlannerSection title={`Extras · ${q.extras.length} line${q.extras.length === 1 ? "" : "s"}`} color="#7C3AED">
            <PlannerTable
              accent="#7C3AED"
              headers={["Description", "Cost"]}
              rightCols={[1]}
            >
              {(q.extras || []).map((ex, i) => (
                <tr key={`ex-${i}`} className={i % 2 === 1 ? "bg-slate-50" : ""}>
                  <td className="px-2 py-1.5">{ex.description || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{gbp(Number(ex.cost || 0))}</td>
                </tr>
              ))}
              <tr style={{ background: "#0E1A33", color: "#fff" }}>
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] font-semibold">Extras total</td>
                <td className="px-2 py-2 text-right font-mono font-bold">{gbp(q.extras_total || (q.extras || []).reduce((s, ex) => s + Number(ex.cost || 0), 0))}</td>
              </tr>
            </PlannerTable>
          </PlannerSection>
        )}

        {/* CONTINGENCY + ADMIN CHARGE -------------------------------- */}
        {(Number(q.contingency_sundry_amount) > 0 || Number(q.administration_amount) > 0) && (
          <div
            className="px-4 py-3 rounded border text-[12px]"
            style={{ borderColor: "#0E1A33", background: "#fff" }}
            data-testid="planner-contingency-admin"
            data-pdf-section="true"
          >
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: "#0E1A33" }}>Auto-applied charges</div>
            <table className="w-full text-[12px]">
              <tbody>
                {Number(q.contingency_sundry_amount) > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="py-1 text-slate-700">Contingency Sundry ({Number(q.contingency_sundry_percent || 0).toFixed(2)}%)</td>
                    <td className="py-1 text-right font-mono font-semibold text-slate-900" data-testid="planner-contingency-amount">{gbp(q.contingency_sundry_amount)}</td>
                  </tr>
                )}
                {Number(q.administration_amount) > 0 && (
                  <tr>
                    <td className="py-1 text-slate-700">Administration Charge ({Number(q.administration_percent || 0).toFixed(2)}%)</td>
                    <td className="py-1 text-right font-mono font-semibold text-slate-900" data-testid="planner-admin-amount">{gbp(q.administration_amount)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* FOOTER SUMMARY ---------------------------------------------- */}
        <div
          className="grid grid-cols-5 gap-3 px-4 py-3 rounded text-white"
          style={{ background: "#0E1A33" }}
          data-pdf-section="true"
        >
          <FooterStat label="Labour" v={labourTotal} />
          <FooterStat label="Materials" v={partsTotal} />
          <FooterStat label="Hire" v={hireTotal} />
          <FooterStat label="Access Hire" v={q.access_hire_total || 0} />
          <FooterStat label={costSettings?.sell_label || "Job Grand Total"} v={q.grand_total} highlight />
        </div>

        {/* Internal cost row — printed below the sell total so estimators can
            paste both into the CRM. Uses the editable cost_label from settings. */}
        <div
          className="grid grid-cols-2 gap-3 px-4 py-2.5 rounded border-2 text-[12px]"
          style={{ borderColor: "#0E1A33", background: "#F1F5F9" }}
          data-testid="planner-cost-row"
          data-pdf-section="true"
        >
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: "#0E1A33" }}>
            {costSettings?.cost_label || "Cost"} (internal)
          </div>
          <div className="text-right font-mono text-lg font-bold" style={{ color: "#0E1A33" }} data-testid="planner-total-cost">
            {gbp(q.total_cost || 0)}
          </div>
        </div>

        <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 text-center pt-2">
          {footerText
            ? footerText
            : `Entry Group · Quote Matrix · ${q.quote_number} · Generated ${new Date().toLocaleDateString("en-GB")}`}
        </div>
      </div>
    </article>
  );
}

function Tile({ label, value, accent, big }) {
  return (
    <div className="rounded border px-3 py-2 bg-white" style={{ borderColor: accent + "30" }}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-semibold" style={{ color: accent }}>{label}</div>
      <div className={`font-mono font-bold mt-0.5 text-slate-900 ${big ? "text-2xl" : "text-lg"}`}>{value}</div>
    </div>
  );
}

function PlannerSection({ title, color, children }) {
  return (
    <section className="break-inside-avoid" data-pdf-section="true">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-1.5 h-5 rounded-sm" style={{ background: color }} />
        <h3 className="text-[13px] font-bold uppercase tracking-wider" style={{ color }}>{title}</h3>
      </div>
      {children}
    </section>
  );
}

function PlannerTable({ headers, rightCols = [], accent, children }) {
  return (
    <div className="border border-slate-300 rounded overflow-hidden">
      <table className="w-full text-[12px]">
        <thead>
          <tr style={{ background: accent, color: "#fff" }}>
            {headers.map((h, i) => (
              <th key={h} className={`px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${rightCols.includes(i) ? "text-right" : "text-left"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function FooterStat({ label, v, highlight }) {
  return (
    <div className={highlight ? "border-l-2 pl-3" : "pl-3"} style={highlight ? { borderColor: "#E66A2C" } : {}}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className={`font-mono font-bold ${highlight ? "text-xl" : "text-base"}`} style={highlight ? { color: "#E66A2C" } : {}}>
        {gbp(v || 0)}
      </div>
    </div>
  );
}
